/**
 * Limita a 5 unidades por variante/referencia en el mismo carrito.
 * Intercepta /cart/add.js, /cart/change.js y /cart/update.js y corrige líneas existentes.
 */
(() => {
  const MAX_PER_REFERENCE = 5;
  const MESSAGE = `Máximo ${MAX_PER_REFERENCE} unidades por referencia en el mismo pedido.`;

  const cartEndpoints = ['/cart/add', '/cart/change', '/cart/update'];

  function isCartUrl(url) {
    try {
      const path = typeof url === 'string' ? new URL(url, window.location.origin).pathname : '';
      return cartEndpoints.some((endpoint) => path === endpoint || path === `${endpoint}.js`);
    } catch {
      return false;
    }
  }

  async function fetchCart() {
    const response = await fetch('/cart.js', { credentials: 'same-origin' });
    if (!response.ok) throw new Error('No se pudo leer el carrito');
    return response.json();
  }

  function quantityInCart(cart, variantId) {
    if (!cart || !variantId) return 0;
    const id = Number(variantId);
    return (cart.items || []).reduce((sum, item) => {
      if (Number(item.variant_id) === id || Number(item.id) === id) {
        return sum + Number(item.quantity || 0);
      }
      return sum;
    }, 0);
  }

  function notify(message) {
    document.dispatchEvent(
      new CustomEvent('alvic:max-quantity', {
        detail: { message },
        bubbles: true,
      })
    );
    try {
      console.warn('[alvic]', message);
    } catch {
      /* noop */
    }
  }

  async function clampExistingCart() {
    const cart = await fetchCart();
    const overLimit = (cart.items || []).filter((item) => Number(item.quantity) > MAX_PER_REFERENCE);
    if (!overLimit.length) return cart;

    const updates = {};
    for (const item of overLimit) {
      updates[String(item.key || item.variant_id)] = MAX_PER_REFERENCE;
    }
    await fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates }),
    });
    notify(MESSAGE);
    return fetchCart();
  }

  async function normalizeAddPayload(body, cart) {
    let payload;
    if (typeof body === 'string') {
      try {
        payload = JSON.parse(body);
      } catch {
        const params = new URLSearchParams(body);
        payload = Object.fromEntries(params.entries());
        if (payload.id) payload.id = Number(payload.id);
        if (payload.quantity) payload.quantity = Number(payload.quantity);
      }
    } else if (body instanceof FormData) {
      payload = Object.fromEntries(body.entries());
      if (payload.id) payload.id = Number(payload.id);
      if (payload.quantity) payload.quantity = Number(payload.quantity);
    } else if (body && typeof body === 'object') {
      payload = { ...body };
    } else {
      return { body, limited: false };
    }

    let limited = false;

    const clampItem = (item) => {
      const variantId = item.id || item.variant_id;
      if (!variantId) return item;
      const already = quantityInCart(cart, variantId);
      const requested = Number(item.quantity ?? 1);
      const allowed = Math.max(0, MAX_PER_REFERENCE - already);
      if (requested > allowed) {
        limited = true;
        return { ...item, quantity: allowed };
      }
      return item;
    };

    if (Array.isArray(payload.items)) {
      payload.items = payload.items.map(clampItem).filter((item) => Number(item.quantity) > 0);
      if (!payload.items.length) limited = true;
    } else if (payload.id || payload.variant_id) {
      const clamped = clampItem(payload);
      if (Number(clamped.quantity) <= 0) {
        limited = true;
        payload = { ...clamped, quantity: 0 };
      } else {
        payload = clamped;
      }
    }

    return {
      body: JSON.stringify(payload),
      limited,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      abortAdd: Array.isArray(payload.items) ? payload.items.length === 0 : Number(payload.quantity) <= 0,
    };
  }

  async function normalizeChangePayload(body, cart) {
    let payload;
    if (typeof body === 'string') {
      try {
        payload = JSON.parse(body);
      } catch {
        const params = new URLSearchParams(body);
        payload = Object.fromEntries(params.entries());
      }
    } else if (body instanceof FormData) {
      payload = Object.fromEntries(body.entries());
    } else if (body && typeof body === 'object') {
      payload = { ...body };
    } else {
      return { body, limited: false };
    }

    let limited = false;
    if (payload.quantity != null) {
      const qty = Number(payload.quantity);
      if (qty > MAX_PER_REFERENCE) {
        payload.quantity = MAX_PER_REFERENCE;
        limited = true;
      }
    }
    if (payload.updates && typeof payload.updates === 'object') {
      for (const [key, value] of Object.entries(payload.updates)) {
        if (Number(value) > MAX_PER_REFERENCE) {
          payload.updates[key] = MAX_PER_REFERENCE;
          limited = true;
        }
      }
    }

    return {
      body: JSON.stringify(payload),
      limited,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    };
  }

  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input, init = {}) => {
    const url = typeof input === 'string' ? input : input?.url;
    if (!isCartUrl(url) || (init.method || 'GET').toUpperCase() === 'GET') {
      return originalFetch(input, init);
    }

    const path = new URL(url, window.location.origin).pathname;
    try {
      const cart = await fetchCart();
      let nextInit = { ...init };
      let limited = false;
      let abortAdd = false;

      if (path.includes('/cart/add')) {
        const normalized = await normalizeAddPayload(init.body, cart);
        limited = normalized.limited;
        abortAdd = normalized.abortAdd;
        nextInit = {
          ...nextInit,
          body: normalized.body,
          headers: {
            ...(init.headers || {}),
            ...(normalized.headers || {}),
          },
        };
      } else if (path.includes('/cart/change') || path.includes('/cart/update')) {
        const normalized = await normalizeChangePayload(init.body, cart);
        limited = normalized.limited;
        nextInit = {
          ...nextInit,
          body: normalized.body,
          headers: {
            ...(init.headers || {}),
            ...(normalized.headers || {}),
          },
        };
      }

      if (abortAdd) {
        notify(MESSAGE);
        return new Response(
          JSON.stringify({
            status: 422,
            message: MESSAGE,
            description: MESSAGE,
          }),
          { status: 422, headers: { 'Content-Type': 'application/json' } }
        );
      }

      const response = await originalFetch(input, nextInit);
      if (limited) notify(MESSAGE);
      return response;
    } catch (error) {
      return originalFetch(input, init);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      clampExistingCart().catch(() => {});
    });
  } else {
    clampExistingCart().catch(() => {});
  }
})();
